import React, { useState, useRef } from 'react';
import { Button, Input } from './UIComponents';
import { validatePath, uploadContextFiles, UploadContextResponse } from '../services/api';

export interface ContextStatus {
  valid: boolean;
  path?: string;
  filesCount?: number;
  mode: 'cloud' | 'local';
  source: 'upload' | 'path';
  uploadId?: string | null;
  message?: string;
}

interface ContextLoaderProps {
  onPathSelected: (path: string, uploadId?: string) => void;
  currentPath?: string;
  onContextStatusChange?: (status: ContextStatus) => void;
}

// Detect if we're running in cloud mode (not localhost)
const isCloudMode = () => {
  const hostname = window.location.hostname;
  return hostname !== 'localhost' && hostname !== '127.0.0.1';
};

export const ContextLoader: React.FC<ContextLoaderProps> = ({ 
  onPathSelected, 
  currentPath,
  onContextStatusChange,
}) => {
  const [path, setPath] = useState(currentPath || '');
  const [validating, setValidating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationMessage, setValidationMessage] = useState('');
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const cloudMode = isCloudMode();

  const emitContextStatus = (status: ContextStatus) => {
    if (!onContextStatusChange) return;
    onContextStatusChange(status);
  };

  const handleValidatePath = async () => {
    if (!path.trim()) {
      setValidationMessage('Por favor ingresa una ruta');
      setIsValid(false);
      emitContextStatus({
        valid: false,
        path,
        mode: cloudMode ? 'cloud' : 'local',
        source: cloudMode ? 'upload' : 'path',
        message: 'Ruta vacía',
      });
      return;
    }

    setValidating(true);
    setValidationMessage('');
    setIsValid(null);

    try {
      const result = await validatePath(path);
      
      if (result.valid) {
        // Validar límite de 30 archivos
        if (result.files_count > 30) {
          setIsValid(false);
          setValidationMessage(
            `✕ Límite excedido: ${result.files_count} archivos encontrados. Máximo permitido: 30 archivos.\n` +
            `💡 Selecciona una carpeta con los archivos más importantes para el análisis.`
          );
          emitContextStatus({
            valid: false,
            path,
            filesCount: result.files_count,
            mode: cloudMode ? 'cloud' : 'local',
            source: cloudMode ? 'upload' : 'path',
            message: 'Límite de archivos excedido',
          });
          return;
        }
        
        setIsValid(true);
        setValidationMessage(
          `✓ Ruta válida - ${result.files_count} archivos encontrados`
        );
        onPathSelected(path);
        emitContextStatus({
          valid: true,
          path,
          filesCount: result.files_count,
          mode: cloudMode ? 'cloud' : 'local',
          source: cloudMode ? 'upload' : 'path',
          message: 'Ruta validada',
        });
      } else if (!result.exists) {
        setIsValid(false);
        setValidationMessage('✕ La ruta no existe');
        emitContextStatus({
          valid: false,
          path,
          mode: cloudMode ? 'cloud' : 'local',
          source: cloudMode ? 'upload' : 'path',
          message: 'La ruta no existe',
        });
      } else if (!result.is_directory) {
        setIsValid(false);
        setValidationMessage('✕ La ruta no es un directorio válido');
        emitContextStatus({
          valid: false,
          path,
          mode: cloudMode ? 'cloud' : 'local',
          source: cloudMode ? 'upload' : 'path',
          message: 'Ruta no es directorio',
        });
      } else {
        setIsValid(false);
        setValidationMessage('✕ Ruta inválida');
        emitContextStatus({
          valid: false,
          path,
          mode: cloudMode ? 'cloud' : 'local',
          source: cloudMode ? 'upload' : 'path',
          message: 'Ruta inválida',
        });
      }
    } catch (error) {
      setIsValid(false);
      setValidationMessage('✕ Error al validar la ruta');
      console.error('Path validation error:', error);
      emitContextStatus({
        valid: false,
        path,
        mode: cloudMode ? 'cloud' : 'local',
        source: cloudMode ? 'upload' : 'path',
        message: 'Error al validar la ruta',
      });
    } finally {
      setValidating(false);
    }
  };

  const handleSelectFolder = () => {
    if (cloudMode) {
      // In cloud mode, use the hidden file input to trigger folder upload
      fileInputRef.current?.click();
    } else {
      // Local mode - original behavior for Electron/local apps
      const input = document.createElement('input');
      input.type = 'file';
      input.setAttribute('accept', '*/*');  // Accept all file types
      // @ts-ignore - webkitdirectory is not in TypeScript definitions
      input.webkitdirectory = true;
      // @ts-ignore
      input.directory = true;
      input.multiple = true;  // Explicitly set multiple
      
      input.onchange = async (e: any) => {
        const files = e.target.files;
        if (files && files.length > 0) {
          const filePath = files[0].path;
          if (filePath) {
            const dirPath = filePath.substring(0, filePath.lastIndexOf('\\') || filePath.lastIndexOf('/'));
            setPath(dirPath);
            
            setValidating(true);
            setValidationMessage('');
            setIsValid(null);
            
            try {
              const result = await validatePath(dirPath);
              
              if (result.valid) {
                // Validar límite de 30 archivos
                if (result.files_count > 30) {
                  setIsValid(false);
                  setValidationMessage(
                    `✕ Límite excedido: ${result.files_count} archivos encontrados. Máximo permitido: 30 archivos.\n` +
                    `💡 Selecciona una carpeta con los archivos más importantes para el análisis.`
                  );
                  emitContextStatus({
                    valid: false,
                    path: dirPath,
                    filesCount: result.files_count,
                    mode: cloudMode ? 'cloud' : 'local',
                    source: cloudMode ? 'upload' : 'path',
                    message: 'Límite de archivos excedido',
                  });
                  return;
                }
                
                setIsValid(true);
                setValidationMessage(
                  `✓ Ruta válida - ${result.files_count} archivos encontrados`
                );
                onPathSelected(dirPath);
                emitContextStatus({
                  valid: true,
                  path: dirPath,
                  filesCount: result.files_count,
                  mode: cloudMode ? 'cloud' : 'local',
                  source: cloudMode ? 'upload' : 'path',
                  message: 'Ruta validada',
                });
              } else {
                setIsValid(false);
                setValidationMessage('✕ Ruta inválida');
                emitContextStatus({
                  valid: false,
                  path: dirPath,
                  mode: cloudMode ? 'cloud' : 'local',
                  source: cloudMode ? 'upload' : 'path',
                  message: 'Ruta inválida',
                });
              }
            } catch (error) {
              setIsValid(false);
              setValidationMessage('✕ Error al validar la ruta');
              emitContextStatus({
                valid: false,
                path: dirPath,
                mode: cloudMode ? 'cloud' : 'local',
                source: cloudMode ? 'upload' : 'path',
                message: 'Error al validar la ruta',
              });
            } finally {
              setValidating(false);
            }
          }
        }
      };
      
      input.click();
    }
  };

  // Handle file upload for cloud mode
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      console.log('[ContextLoader] No files selected');
      return;
    }

    // VALIDACIÓN: Máximo 30 archivos
    if (files.length > 30) {
      setIsValid(false);
      setValidationMessage(
        `✕ Límite excedido: ${files.length} archivos. Máximo permitido: 30 archivos.\n` +
        `💡 Selecciona solo los archivos más importantes para el análisis.`
      );
      emitContextStatus({
        valid: false,
        filesCount: files.length,
        mode: cloudMode ? 'cloud' : 'local',
        source: cloudMode ? 'upload' : 'path',
        message: 'Límite de archivos excedido',
      });
      // Resetear el input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    console.log(`[ContextLoader] Starting upload of ${files.length} files`);
    console.log(`[ContextLoader] User Agent: ${navigator.userAgent}`);
    console.log(`[ContextLoader] Platform: ${navigator.platform}`);
    
    // Debug logging for Windows compatibility
    console.log('[ContextLoader] File details:');
    const fileExtensions = new Set<string>();
    const totalSize = Array.from(files).reduce((sum, file) => sum + file.size, 0);
    
    Array.from(files).slice(0, 10).forEach((file, idx) => {
      const ext = file.name.split('.').pop() || 'no-ext';
      fileExtensions.add(ext);
      const webkitPath = (file as any).webkitRelativePath;
      console.log(`  [${idx}] ${file.name} (${file.size} bytes, type: ${file.type || 'unknown'}, path: ${webkitPath || 'N/A'})`);
    });
    console.log(`[ContextLoader] Unique extensions found: ${Array.from(fileExtensions).join(', ')}`);
    console.log(`[ContextLoader] Total size: ${(totalSize / 1024 / 1024).toFixed(2)} MB`);
    
    setUploading(true);
    setUploadProgress(0);
    setValidationMessage('Preparando archivos...');
    setIsValid(null);

    try {
      // Get directory name from first file's relative path
      const firstFile = files[0] as any;
      let relativePath = firstFile.webkitRelativePath || firstFile.name;
      
      // Normalize path separator for Windows (both backslash variants)
      relativePath = relativePath.replace(/\\/g, '/').replace(/\\\\/g, '/');
      
      // Extract directory name (first part of the path)
      const pathParts = relativePath.split('/').filter((p: string) => p.length > 0);
      const dirName = pathParts[0] || 'uploaded-context';
      
      console.log(`[ContextLoader] Original path: ${firstFile.webkitRelativePath}`);
      console.log(`[ContextLoader] Normalized path: ${relativePath}`);
      console.log(`[ContextLoader] Repository name: ${dirName}`);
      
      setValidationMessage(`Subiendo ${files.length} archivos...`);

      const result: UploadContextResponse = await uploadContextFiles(
        files,
        dirName,
        (percent) => {
          setUploadProgress(percent);
          setValidationMessage(`Subiendo archivos... ${percent}%`);
        }
      );

      if (result.success) {
        setIsValid(true);
        setUploadId(result.upload_id);
        setPath(result.path);
        setValidationMessage(
          `✓ Subidos ${result.files_count} archivos correctamente`
        );
        console.log(`[ContextLoader] Upload successful: ${result.upload_id}`);
        onPathSelected(result.path, result.upload_id);
        emitContextStatus({
          valid: true,
          path: result.path,
          filesCount: result.files_count,
          mode: cloudMode ? 'cloud' : 'local',
          source: 'upload',
          uploadId: result.upload_id,
          message: 'Archivos cargados y contexto validado',
        });
      } else {
        setIsValid(false);
        const errorMsg = result.errors && result.errors.length > 0 
          ? `Errores: ${result.errors.join(', ')}`
          : 'Error al subir archivos';
        setValidationMessage(`✕ ${errorMsg}`);
        console.error('[ContextLoader] Upload failed:', result);
        emitContextStatus({
          valid: false,
          mode: cloudMode ? 'cloud' : 'local',
          source: 'upload',
          message: errorMsg,
        });
      }
    } catch (error: any) {
      setIsValid(false);
      const errorMessage = error.message || 'Error al subir archivos';
      setValidationMessage(`✕ ${errorMessage}`);
      console.error('[ContextLoader] Upload error:', error);
      emitContextStatus({
        valid: false,
        mode: cloudMode ? 'cloud' : 'local',
        source: 'upload',
        message: errorMessage,
      });
      
      // Log additional debug info
      if (error.response) {
        console.error('[ContextLoader] Server response:', error.response.data);
      } else if (error.request) {
        console.error('[ContextLoader] No response received');
      }
    } finally {
      setUploading(false);
      // Keep progress visible for a moment before resetting
      setTimeout(() => setUploadProgress(0), 2000);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h3 className="text-xl font-bold text-gft-dark mb-4">
        Cargar Contexto
      </h3>
      
      {cloudMode && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
          <span className="font-semibold">☁️ Modo Cloud:</span> Sube tus archivos desde tu equipo local
        </div>
      )}
      
      {/* Hidden file input for cloud mode folder upload */}
      <input
        ref={fileInputRef}
        type="file"
        // @ts-ignore - webkitdirectory and directory are not in standard TypeScript definitions
        webkitdirectory="true"
        directory="true"
        multiple
        accept="*/*"  // Explicitly accept all file types (fixes Windows filter issue)
        onChange={handleFileUpload}
        className="hidden"
      />
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            {cloudMode 
              ? 'Selecciona la carpeta con documentación para subir'
              : 'Ruta del directorio con documentación'
            }
          </label>
          
          {cloudMode ? (
            // Cloud mode UI
            <div className="flex flex-col gap-3">
              <Button
                variant="primary"
                onClick={handleSelectFolder}
                loading={uploading}
                className="w-full py-3"
              >
                {uploading ? (
                  <span>Subiendo... {uploadProgress}%</span>
                ) : (
                  <span>📁 Seleccionar Carpeta y Subir</span>
                )}
              </Button>
              
              {uploading && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gft-purple h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              )}
              
              {uploadId && (
                <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                  <span className="font-semibold">✓ Contexto cargado</span>
                  <br />
                  <span className="text-xs">ID: {uploadId}</span>
                </div>
              )}
            </div>
          ) : (
            // Local mode UI
            <div className="flex gap-2">
              <Input
                type="text"
                value={path}
                onChange={(e) => {
                  setPath(e.target.value);
                  setIsValid(null);
                  setValidationMessage('');
                }}
                placeholder="C:\mi-proyecto\documentacion"
                className="flex-1"
              />
              <Button
                variant="outline"
                onClick={handleSelectFolder}
                className="whitespace-nowrap"
              >
                Explorar
              </Button>
            </div>
          )}
          
          {validationMessage && (
            <p className={`mt-2 text-sm ${isValid ? 'text-green-600' : 'text-red-600'}`}>
              {validationMessage}
            </p>
          )}
        </div>

        {!cloudMode && (
          <Button
            onClick={handleValidatePath}
            loading={validating}
            disabled={!path.trim()}
            className="w-full"
          >
            Validar Ruta
          </Button>
        )}

        <div className="bg-purple-50 rounded-lg p-4 text-sm text-gray-700">
          <p className="font-semibold mb-2">💡 Formatos soportados:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Documentos: .txt, .md, .pdf, .docx</li>
            <li>Código: .py, .js, .ts, .json, .yaml</li>
            <li>Web: .html, .css, .xml</li>
            <li>Otros: .csv, .sql, .log</li>
          </ul>
          
          {/* Warning de límite de archivos */}
          <div className="mt-4 p-3 bg-yellow-100 border-l-4 border-yellow-500 rounded">
            <p className="font-bold text-yellow-800 mb-2">⚠️ Límite: Máximo 30 archivos</p>
            <p className="text-yellow-900 text-xs leading-relaxed">
              El sistema está optimizado para analizar hasta <strong>30 archivos</strong> 
              para garantizar calidad y rendimiento óptimo.
            </p>
          </div>
          
          {/* Recomendación de Markdown */}
          <div className="mt-3 p-3 bg-blue-100 border-l-4 border-blue-500 rounded">
            <p className="font-bold text-blue-800 mb-2">📝 Mejor Práctica Recomendada</p>
            <p className="text-blue-900 text-xs leading-relaxed mb-2">
              Para obtener los <strong>mejores resultados</strong>, documenta tus procesos en formato <strong>Markdown (.md)</strong>:
            </p>
            <ul className="list-disc list-inside text-blue-900 text-xs space-y-1 ml-2">
              <li>Estructura clara con encabezados (# ## ###)</li>
              <li>Tablas para flujos y reglas de negocio</li>
              <li>Listas para requisitos y criterios</li>
              <li>Código con sintaxis destacada</li>
            </ul>
            <p className="text-blue-800 text-xs mt-2 font-semibold">
              ✨ El agente y LLM procesan Markdown de forma más efectiva que otros formatos
            </p>
          </div>
          
          {cloudMode && (
            <p className="mt-3 text-blue-700">
              <strong>Nota:</strong> Los archivos se subirán temporalmente al servidor para ser procesados.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContextLoader;
